import React, { useState } from 'react';
import {
  Grid,
  Button,
  Chip,
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Avatar,
  ListItemText,
  ListItemAvatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FilterListIcon from '@material-ui/icons/FilterList';
import ClearIcon from '@material-ui/icons/Clear';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';

const useStyles = makeStyles(theme => ({
  filterCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  filterIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  activeFiltersRow: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  filterChip: {
    margin: theme.spacing(0.5),
  },
  clearAllButton: {
    marginLeft: theme.spacing(1),
  },
  crewMenuItem: {
    padding: theme.spacing(1),
    minHeight: 60,
  },
  crewAvatar: {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    fontSize: '0.875rem',
  },
  crewInfo: {
    marginLeft: theme.spacing(1),
  },
  crewDivision: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  crewName: {
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  crewForeman: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  crewChip: {
    '& .MuiChip-avatar': {
      width: 24,
      height: 28,
    },
  },
  crewChipAvatar: {
    backgroundColor: theme.palette.primary.main,
    fontSize: '0.75rem',
    width: 22,
    height: 22,
  },
}));

const FilterBar = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  sites = [],
  resultCount = 0,
  crews
}) => {
  const classes = useStyles();

  // Predefined day order
  const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Predefined frequency order
  const frequencyOrder = [
    'Daily',
    'Every Other Day (M-W-F)',
    'Twice a Week',
    'Weekly',
    'Bi-weekly',
    'Monthly',
    'Quarterly',
    'Semi-annually',
    'Annually',
    'On Demand'
  ];

  // Get unique values from sites for dynamic filter options
  const getUniqueValues = (field) => {
    const values = sites
      .map(site => site[field])
      .filter(value => value && value !== '')
      .map(value => typeof value === 'string' ? value.trim() : value);
    return [...new Set(values)];
  };

  const customerTypes = getUniqueValues('customerType').sort();
  
  // Sort frequencies by predefined order
  const frequencies = getUniqueValues('frequency').sort((a, b) => {
    const indexA = frequencyOrder.findIndex(freq => freq.toLowerCase() === a.toLowerCase());
    const indexB = frequencyOrder.findIndex(freq => freq.toLowerCase() === b.toLowerCase());
    
    // If both found in order array, sort by index
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only one found, prioritize the one in order
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither found, sort alphabetically
    return a.localeCompare(b);
  });

  // Sort preferred days by weekday order
  const preferredDays = getUniqueValues('preferredDay').sort((a, b) => {
    const indexA = daysOrder.indexOf(a);
    const indexB = daysOrder.indexOf(b);
    
    // If both found in order array, sort by index
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only one found, prioritize the one in order
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither found, sort alphabetically
    return a.localeCompare(b);
  });

  // Enhanced crew data with division info
  const crewsFromsites = sites
    .filter(site => site.crew)
    .map(site => {
      // Extract crew info - assuming you might have this data
      const crewName = site.crewName || 'Unknown Crew';
      const parts = crewName.split('(');
      const name = parts[0].trim();
      const foremanName = parts[1] ? parts[1].replace(')', '').trim() : 'TBD';
      
      return {
        id: site.crew,
        name: name,
        foremanName: foremanName,
        
        initials: name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()
      };
    })
    .filter((crew, index, self) => 
      crew._id && self.findIndex(c => c._id === crew._id) === index
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const sortedCrews = crews.sort((a, b) => a.name.localeCompare(b.name));

  const getActiveFilters = () => {
    const active = [];
    
    if (filters.customerType) {
      active.push({ key: 'customerType', label: 'Customer Type', value: filters.customerType });
    }
    if (filters.frequency) {
      active.push({ key: 'frequency', label: 'Frequency', value: filters.frequency });
    }
    if (filters.preferredDay) {
      active.push({ key: 'preferredDay', label: 'Preferred Day', value: filters.preferredDay });
    }
    if (filters.crew) {
      const crew = crews.find(c => c._id === filters.crew);
      console.log('Crew...', crew)
      const crewLabel = crew ? `${crew.name} (${crew.foremanName})` : filters.crew;
      active.push({ key: 'crew', label: 'Crew', value: crewLabel, photoUrl: crew?.photoUrl });
    }
    if (filters.evenOrOddWeeks) {
      active.push({ key: 'evenOrOddWeeks', label: 'Even/Odd Weeks', value: filters.evenOrOddWeeks });
    }
    if (filters.assignmentStatus) {
      active.push({ key: 'assignmentStatus', label: 'Assignment Status', value: filters.assignmentStatus });
    }
    if (filters.overdueSiteReview) {
      active.push({ key: 'overdueSiteReview', label: 'Site Review Due', value: filters.overdueSiteReview });
    }
    if (filters.overdueService) {
      active.push({ key: 'overdueService', label: 'Service Overdue', value: filters.overdueService });
    }
    
    
    return active;
  };

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // Custom crew menu item renderer with profile photo
  const renderCrewMenuItem = (crew) => {
    // console.log('Render crew item', crew)
    const profilePhoto = crew?.photoUrl || crew?.foreman?.profile_photo_url
  return (
    <MenuItem 
      key={crew._id} 
      value={crew._id} 
      className={classes.crewMenuItem}
    >
      <ListItemAvatar>
        <Avatar 
          className={classes.crewAvatar}
          src={profilePhoto} // Profile photo URL
          alt={crew.foremanName}
        >
          {/* Fallback to initials if no photo */}
          {!profilePhoto && crew.initials}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box>
            <Typography className={classes.crewDivision}>
              {crew.division}
            </Typography>
            <Typography className={classes.crewName}>
              {crew.name} ({crew.foremanName})
            </Typography>
            {/* <Typography className={classes.crewForeman}>
              Foreman: {crew.foremanName}
            </Typography> */}
          </Box>
        }
      />
    </MenuItem>
  )};

  return (
    <Paper className={classes.filterCard}>
      <div className={classes.filterHeader}>
        <Box display="flex" alignItems="center">
          <FilterListIcon className={classes.filterIcon} />
          <Typography variant="h6">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
              ({resultCount} sites)
            </Typography>
          )}
        </Box>
        {hasActiveFilters && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClearFilters}
            startIcon={<ClearIcon />}
            className={classes.clearAllButton}
          >
            Clear All
          </Button>
        )}
      </div>

      <Grid container spacing={2} alignItems="flex-start">
        {/* Customer Type Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Customer Type"
            value={filters.customerType || ''}
            onChange={(e) => onFilterChange('customerType', e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            {customerTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Frequency Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Frequency"
            value={filters.frequency || ''}
            onChange={(e) => onFilterChange('frequency', e.target.value)}
          >
            <MenuItem value="">All Frequencies</MenuItem>
            {frequencies.map(freq => (
              <MenuItem key={freq} value={freq}>{freq}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Preferred Day Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Preferred Day"
            value={filters.preferredDay || ''}
            onChange={(e) => onFilterChange('preferredDay', e.target.value)}
          >
            <MenuItem value="">All Days</MenuItem>
            {preferredDays.map(day => (
              <MenuItem key={day} value={day}>{day}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Enhanced Crew Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Crew"
            value={filters.crew || ''}
            onChange={(e) => onFilterChange('crew', e.target.value)}
            SelectProps={{
              renderValue: (selected) => {
                if (!selected) return 'All Crews';
                if (selected === 'unassigned') return 'Unassigned Sites';
                const crew = crews.find(c => c._id === selected);
                return crew ? `${crew.name} (${crew?.foremanName})` : selected;
              },
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              },
            }}
          >
            <MenuItem value="">All Crews</MenuItem>
            <MenuItem value="unassigned" className={classes.crewMenuItem}>
              <ListItemAvatar>
                <Avatar style={{ backgroundColor: '#f44336', width: 40, height: 40 }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Box>
                    <Typography className={classes.crewName}>
                      Unassigned Sites
                    </Typography>
                    <Typography className={classes.crewForeman}>
                      No crew assigned
                    </Typography>
                  </Box>
                }
              />
            </MenuItem>
            {sortedCrews.map(crew => renderCrewMenuItem(crew))}
          </TextField>
        </Grid>

        {/* Even/Odd Weeks Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Even/Odd Weeks"
            value={filters.evenOrOddWeeks || ''}
            onChange={(e) => onFilterChange('evenOrOddWeeks', e.target.value)}
          >
            <MenuItem value="">All Schedules</MenuItem>
            <MenuItem value="Even">Even Weeks</MenuItem>
            <MenuItem value="Odd">Odd Weeks</MenuItem>
            <MenuItem value="Not Set">Not Set</MenuItem>
          </TextField>
        </Grid>

        {/* Assignment Status Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Assignment Status"
            value={filters.assignmentStatus || ''}
            onChange={(e) => onFilterChange('assignmentStatus', e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="Assigned">Assigned</MenuItem>
            <MenuItem value="Unassigned">Unassigned</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {hasActiveFilters && (
          <Box className={classes.activeFiltersRow}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Active Filters:
            </Typography>
            <Box display="flex" flexWrap="wrap">
              {activeFilters.map(filter => {
                console.log('What kind of filter??', filter)
                // Special handling for crew filters to show photo
                if (filter.key === 'crew') {
                  console.log('should we show the avatar photo??', filter)
                  return (
                    <Chip
                      key={filter.key}
                      avatar={
                        filter.photoUrl ? (
                          <Avatar 
                            src={filter.photoUrl} 
                            className={classes.crewChipAvatar}
                            alt={filter.value}
                          />
                        ) : filters.crew === 'unassigned' ? (
                          <Avatar className={classes.crewChipAvatar}>
                            <PersonIcon style={{ fontSize: '0.875rem' }} />
                          </Avatar>
                        ) : (
                          <Avatar className={classes.crewChipAvatar}>
                            {filter.value.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()}
                          </Avatar>
                        )
                      }
                      label={`${filter.label}: ${filter.value}`}
                      onDelete={() => onFilterChange(filter.key, '')}
                      color="primary"
                      variant="outlined"
                      size="small"
                      className={`${classes.filterChip} ${classes.crewChip}`}
                    />
                  );
                }
                
                // Regular chips for other filters
                return (
                  <Chip
                    key={filter.key}
                    label={`${filter.label}: ${filter.value}`}
                    onDelete={() => onFilterChange(filter.key, '')}
                    color="primary"
                    variant="outlined"
                    size="small"
                    className={classes.filterChip}
                  />
                );
              })}
            </Box>
          </Box>
        )}

      {/* {hasActiveFilters && (
        <Box className={classes.activeFiltersRow}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Active Filters:
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={`${filter.label}: ${filter.value}`}
                onDelete={() => onFilterChange(filter.key, '')}
                color="primary"
                variant="outlined"
                size="small"
                className={classes.filterChip}
              />
            ))}
          </Box>
        </Box>
      )} */}
    </Paper>
  );
};

export default FilterBar
